const BASE = "";

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number
  ) {
    super(message);
  }
}

const HTTP_ERRORS: Record<number, string> = {
  400: "요청 데이터가 올바르지 않습니다.",
  401: "로그인이 필요합니다.",
  403: "이 작업에 대한 권한이 없습니다.",
  404: "데이터를 찾을 수 없습니다.",
  409: "이미 존재하는 데이터입니다.",
  422: "입력값이 올바르지 않습니다.",
  500: "서버 오류가 발생했습니다. 잠시 후 다시 시도해 주세요.",
};

function extractErrorMessage(body: unknown, statusCode: number): string {
  if (body && typeof body === "object") {
    const b = body as Record<string, unknown>;
    // 일반 { error: string } 형식
    if (typeof b.error === "string") return b.error;
    // Zod validation error: { success: false, error: { issues: [...] } }
    if (b.error && typeof b.error === "object") {
      const err = b.error as Record<string, unknown>;
      if (Array.isArray(err.issues) && err.issues.length > 0) {
        const firstIssue = err.issues[0] as Record<string, unknown>;
        const field = Array.isArray(firstIssue.path) ? firstIssue.path.join(".") : "";
        const msg = typeof firstIssue.message === "string" ? firstIssue.message : "유효하지 않은 값";
        return field ? `"${field}" 필드: ${msg}` : msg;
      }
    }
    // { message: string } 형식
    if (typeof b.message === "string") return b.message;
  }
  return HTTP_ERRORS[statusCode] ?? "알 수 없는 오류가 발생했습니다.";
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    credentials: "include",
    headers: { "Content-Type": "application/json", ...init?.headers },
    ...init,
  });
  if (!res.ok) {
    const body = await res.json().catch(() => null);
    throw new ApiError(extractErrorMessage(body, res.status), res.status);
  }
  return res.json() as Promise<T>;
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: "POST", body: body !== undefined ? JSON.stringify(body) : undefined }),
  put: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: "PUT", body: body !== undefined ? JSON.stringify(body) : undefined }),
  patch: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: "PATCH", body: body !== undefined ? JSON.stringify(body) : undefined }),
  delete: <T>(path: string) =>
    request<T>(path, { method: "DELETE" }),
  postForm: <T>(path: string, body: FormData) =>
    request<T>(path, {
      method: "POST",
      body,
      headers: {}, // Let browser set Content-Type with boundary
    }),
};

// ─── Tenant types ──────────────────────────────────────────────────────────
// 백엔드 packages/db/src/types.ts의 Tenant와 일치

export type TenantStatus = "active" | "inactive";

export type Tenant = {
  id: string;
  slug: string;
  status: TenantStatus;
  name: string;             // JSON string: {"ko":"봉은사","en":"Bongeunsa",...}
  card_image_url: string | null;
  card_title: string;       // JSON string
  card_body: string;        // JSON string
  card_footnote: string;    // JSON string
  card_button_label: string;// JSON string
  card_button_url: string | null;
  display_order: number;
  theme: string;
  deleted_at: string | null;
  created_at: string;
  updated_at: string;
};

export type TenantI18nField = { ko: string; en: string; zh: string; ja: string };

export type TenantAdmin = {
  user_id: string;
  tenant_id: string;
  admin_email: string;
  created_at: string;
};

export type CreateTenantData = {
  slug: string;
  name: TenantI18nField;
  status?: TenantStatus;
  card_image_url?: string | null;
  card_title?: TenantI18nField;
  card_body?: TenantI18nField;
  card_footnote?: TenantI18nField;
  card_button_label?: TenantI18nField;
  card_button_url?: string;
  display_order?: number;
};

export type UpdateTenantData = Partial<Omit<CreateTenantData, "slug"> & { theme: string }>;

// ─── Tenant API functions ──────────────────────────────────────────────────

export const tenantApi = {
  list: (filter?: "all" | "active" | "inactive") => {
    const qs = filter && filter !== "all" ? `?filter=${filter}` : "";
    return api.get<Tenant[]>(`/api/super-admin/tenants${qs}`);
  },

  get: (id: string) =>
    api.get<Tenant>(`/api/super-admin/tenants/${id}`),

  create: (data: CreateTenantData) => {
    const body = {
      ...data,
      name: typeof data.name === "object" ? JSON.stringify(data.name) : data.name,
      card_title: data.card_title ? JSON.stringify(data.card_title) : undefined,
      card_body: data.card_body ? JSON.stringify(data.card_body) : undefined,
      card_footnote: data.card_footnote ? JSON.stringify(data.card_footnote) : undefined,
      card_button_label: data.card_button_label ? JSON.stringify(data.card_button_label) : undefined,
    };
    return api.post<Tenant>("/api/super-admin/tenants", body);
  },

  update: (id: string, data: UpdateTenantData) => {
    const body = {
      ...data,
      ...(data.name && { name: JSON.stringify(data.name) }),
      ...(data.card_title && { card_title: JSON.stringify(data.card_title) }),
      ...(data.card_body && { card_body: JSON.stringify(data.card_body) }),
      ...(data.card_footnote && { card_footnote: JSON.stringify(data.card_footnote) }),
      ...(data.card_button_label && { card_button_label: JSON.stringify(data.card_button_label) }),
    };
    return api.put<Tenant>(`/api/super-admin/tenants/${id}`, body);
  },

  setStatus: (id: string, status: TenantStatus) =>
    api.patch<{ ok: boolean }>(`/api/super-admin/tenants/${id}/status`, { status }),

  delete: (id: string) =>
    api.delete<{ ok: boolean }>(`/api/super-admin/tenants/${id}`),

  reorder: (order: { id: string; display_order: number }[]) =>
    api.patch<{ ok: boolean }>("/api/super-admin/tenants/reorder", { order }),

  getAssignments: (tenantId: string) =>
    api.get<TenantAdmin[]>(`/api/super-admin/tenants/${tenantId}/assignments`),

  addAssignment: (tenantId: string, userId: string) =>
    api.post<{ ok: boolean }>(`/api/super-admin/tenants/${tenantId}/assignments`, { userId }),

  removeAssignment: (tenantId: string, userId: string) =>
    api.delete<{ ok: boolean }>(`/api/super-admin/tenants/${tenantId}/assignments/${userId}`),
};

// ─── Custom Domain types ───────────────────────────────────────────────────

export type CustomDomain = {
  id: string;
  domain: string;
  tenant_id: string;
  status: 'pending' | 'verified' | 'failed';
  verified_at: string | null;
  created_at: string;
};

// ─── Custom Domain API functions ───────────────────────────────────────────

export const customDomainApi = {
  list: (tenantId: string) =>
    api.get<CustomDomain[]>(`/api/super-admin/tenants/${tenantId}/domains`),
  add: (tenantId: string, domain: string) =>
    api.post<CustomDomain>(`/api/super-admin/tenants/${tenantId}/domains`, { domain }),
  verify: (tenantId: string, domainId: string) =>
    api.patch<{ ok: boolean }>(`/api/super-admin/tenants/${tenantId}/domains/${domainId}/verify`, {}),
  delete: (tenantId: string, domainId: string) =>
    api.delete<{ ok: boolean }>(`/api/super-admin/tenants/${tenantId}/domains/${domainId}`),
};

// ─── Main Site Settings types ──────────────────────────────────────────────

export interface MainSiteConfig {
  html_title?: Record<string, string>;
  favicon_url?: string;
  intro?: {
    tagline?: Record<string, string>;
    description?: Record<string, string>;
  };
  footer?: {
    name?: Record<string, string>;
    address?: Record<string, string>;
    phone?: Record<string, string>;
    copyright?: Record<string, string>;
  };
  splash?: {
    headline?: Record<string, string>;
    subheadline?: Record<string, string>;
    name?: Record<string, string>;
    bg_color?: string;
    text_color?: string;
    dot_color?: string;
  };
}

export const settingsApi = {
  getMain: () => api.get<MainSiteConfig>("/api/super-admin/settings/main"),
  updateMain: (config: MainSiteConfig) =>
    api.put<{ ok: boolean }>("/api/super-admin/settings/main", config),
};

export type MediaFile = {
  key: string;
  url: string;
  name: string;
  size: number;
  uploaded: string;
};

function postForm<T>(path: string, file: File): Promise<T> {
  const fd = new FormData();
  fd.append("file", file);
  return api.postForm<T>(path, fd);
}

export const tenantMediaApi = {
  list: (tenantId: string) =>
    api.get<{ files: MediaFile[] }>(`/api/super-admin/tenants/${tenantId}/media`),
  upload: (tenantId: string, file: File) =>
    postForm<{ url: string; key: string }>(`/api/super-admin/tenants/${tenantId}/upload`, file),
  delete: (tenantId: string, key: string) =>
    api.delete<{ ok: boolean }>(`/api/super-admin/tenants/${tenantId}/media?key=${encodeURIComponent(key)}`),
};

export const mainMediaApi = {
  list: () =>
    api.get<{ files: MediaFile[] }>("/api/super-admin/settings/media"),
  upload: (file: File) =>
    postForm<{ url: string; key: string }>("/api/super-admin/settings/upload", file),
  delete: (key: string) =>
    api.delete<{ ok: boolean }>(`/api/super-admin/settings/media?key=${encodeURIComponent(key)}`),
};

// ─── Admin types ───────────────────────────────────────────────────────────

export type AdminUser = {
  id: string;
  email: string;
  email_verified_at: string | null;
  created_at: string;
};

export type MyTenant = {
  id: string;
  slug: string;
  name: string;
  status: TenantStatus;
};

// ─── Admin API functions ───────────────────────────────────────────────────

export type AdminTenant = {
  id: string;
  slug: string;
  name: string;
  status: TenantStatus;
};

export const adminApi = {
  /** 슈퍼관리자: 어드민 목록 */
  list: () =>
    api.get<AdminUser[]>("/api/super-admin/admins"),

  /** 슈퍼관리자: 어드민 생성 */
  create: (email: string, password: string) =>
    api.post<AdminUser>("/api/super-admin/admins", { email, password }),

  /** 슈퍼관리자: 어드민 삭제 */
  delete: (id: string) =>
    api.delete<{ ok: boolean }>(`/api/super-admin/admins/${id}`),

  /** 슈퍼관리자: 비밀번호 재설정 이메일 발송 */
  resetPassword: (id: string) =>
    api.post<{ ok: boolean }>(`/api/super-admin/admins/${id}/reset-password`),

  /** 슈퍼관리자: 관리자에 배정된 사찰 목록 */
  getTenants: (adminId: string) =>
    api.get<AdminTenant[]>(`/api/super-admin/admins/${adminId}/tenants`),

  /** 슈퍼관리자: 관리자에 사찰 배정 */
  addTenant: (adminId: string, tenantId: string) =>
    api.post<{ ok: boolean }>(`/api/super-admin/admins/${adminId}/tenants`, { tenantId }),

  /** 슈퍼관리자: 관리자에서 사찰 제거 */
  removeTenant: (adminId: string, tenantId: string) =>
    api.delete<{ ok: boolean }>(`/api/super-admin/admins/${adminId}/tenants/${tenantId}`),
};
